/**
 * WalletScreeningPanel - Crypto Wallet Screening Dashboard
 *
 * Shows:
 * - Wallet addresses screened for the applicant
 * - Risk scores and hit details
 * - Resolution workflow for screening hits
 * - Import from external providers
 */

import React, { useState } from 'react';
import {
  Wallet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Loader2,
  ExternalLink,
  Plus,
  Shield,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  useWalletScreenings,
  useScreenWallet,
  useResolveWalletScreening,
} from '../hooks/useWalletScreening';
import { useToast } from '../contexts/ToastContext';

// Network display config
const networkConfig = {
  ethereum: { label: 'Ethereum', color: '#627EEA' },
  bitcoin: { label: 'Bitcoin', color: '#F7931A' },
  solana: { label: 'Solana', color: '#00FFA3' },
  polygon: { label: 'Polygon', color: '#8247E5' },
  arbitrum: { label: 'Arbitrum', color: '#28A0F0' },
  optimism: { label: 'Optimism', color: '#FF0420' },
  avalanche: { label: 'Avalanche', color: '#E84142' },
  bsc: { label: 'BSC', color: '#F0B90B' },
};

// Status config
const statusConfig = {
  clear: { color: 'success', icon: CheckCircle2, label: 'Clear' },
  pending: { color: 'warning', icon: Clock, label: 'Pending' },
  hit: { color: 'danger', icon: XCircle, label: 'Hit' },
  review_required: { color: 'warning', icon: AlertTriangle, label: 'Review Required' },
};

// Truncate wallet address
function truncateAddress(address) {
  if (!address || address.length < 16) return address;
  return `${address.slice(0, 8)}...${address.slice(-6)}`;
}

// Risk score badge
function RiskBadge({ score }) {
  if (score === null || score === undefined) return null;

  let color = 'success';
  if (score >= 60) color = 'danger';
  else if (score >= 40) color = 'warning';

  return (
    <span className={`wallet-risk-badge wallet-risk-badge--${color}`}>
      {score}
    </span>
  );
}

// Single wallet screening card
function WalletCard({ screening, onResolve }) {
  const [expanded, setExpanded] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolution, setResolution] = useState('');
  const [notes, setNotes] = useState('');

  const config = statusConfig[screening.status] || statusConfig.pending;
  const StatusIcon = config.icon;
  const network = networkConfig[screening.network] || { label: screening.network, color: '#888' };

  const handleResolve = async () => {
    if (!resolution || !notes.trim()) return;
    setResolving(true);
    try {
      await onResolve(screening.id, resolution, notes);
      setExpanded(false);
      setResolution('');
      setNotes('');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className={`wallet-card wallet-card--${config.color}`}>
      <div className="wallet-card__header" onClick={() => setExpanded(!expanded)}>
        <div className="wallet-card__main">
          <div className="wallet-card__address">
            <Wallet size={16} />
            <span className="wallet-card__address-text" title={screening.wallet_address}>
              {truncateAddress(screening.wallet_address)}
            </span>
            <a
              href={`https://etherscan.io/address/${screening.wallet_address}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="wallet-card__explorer-link"
            >
              <ExternalLink size={12} />
            </a>
          </div>
          <div className="wallet-card__meta">
            <span
              className="wallet-card__network"
              style={{ '--network-color': network.color }}
            >
              {network.label}
            </span>
            <span className="wallet-card__provider">
              {screening.provider}
            </span>
          </div>
        </div>
        <div className="wallet-card__status">
          <StatusIcon size={16} />
          <span>{config.label}</span>
          <RiskBadge score={screening.risk_score} />
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="wallet-card__details">
          {screening.hit_count > 0 && (
            <div className="wallet-card__hits">
              <div className="wallet-card__hits-header">
                <AlertTriangle size={14} />
                <span>{screening.hit_count} Hit(s) Found</span>
              </div>
              {screening.hit_types?.length > 0 && (
                <div className="wallet-card__hit-types">
                  {screening.hit_types.map((type, i) => (
                    <span key={i} className="wallet-card__hit-type">{type}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {screening.transaction_count !== null && (
            <div className="wallet-card__stats">
              <div className="wallet-card__stat">
                <span className="wallet-card__stat-label">Transactions</span>
                <span className="wallet-card__stat-value">{screening.transaction_count?.toLocaleString()}</span>
              </div>
              {screening.total_volume_usd !== null && (
                <div className="wallet-card__stat">
                  <span className="wallet-card__stat-label">Volume</span>
                  <span className="wallet-card__stat-value">
                    ${screening.total_volume_usd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
          )}

          {screening.risk_category && (
            <div className="wallet-card__risk-category">
              Risk Category: <strong>{screening.risk_category}</strong>
            </div>
          )}

          {!screening.resolved && screening.status !== 'clear' && (
            <div className="wallet-card__resolution">
              <div className="wallet-card__resolution-title">Resolve Screening</div>
              <select
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="wallet-card__select"
              >
                <option value="">Select resolution...</option>
                <option value="false_positive">False Positive</option>
                <option value="confirmed_risk">Confirmed Risk</option>
                <option value="accepted_risk">Accepted Risk</option>
              </select>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Resolution notes (required)..."
                className="wallet-card__textarea"
                rows={2}
              />
              <button
                onClick={handleResolve}
                disabled={!resolution || !notes.trim() || resolving}
                className="wallet-card__resolve-btn"
              >
                {resolving ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
                Resolve
              </button>
            </div>
          )}

          {screening.resolved && (
            <div className="wallet-card__resolved">
              <CheckCircle2 size={14} />
              <span>Resolved: {screening.resolution_status?.replace('_', ' ')}</span>
              {screening.resolution_notes && (
                <p className="wallet-card__resolved-notes">{screening.resolution_notes}</p>
              )}
            </div>
          )}

          <div className="wallet-card__timestamp">
            Screened: {new Date(screening.screened_at).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

// Add wallet modal
function AddWalletModal({ applicantId, onClose, onSuccess }) {
  const [address, setAddress] = useState('');
  const [network, setNetwork] = useState('ethereum');
  const screenWallet = useScreenWallet();
  const { showToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!address.trim()) return;

    try {
      await screenWallet.mutateAsync({
        applicant_id: applicantId,
        wallet_address: address.trim(),
        network,
      });
      showToast('Wallet screening initiated', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to screen wallet', 'error');
    }
  };

  return (
    <div className="wallet-modal-overlay" onClick={onClose}>
      <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wallet-modal__header">
          <Wallet size={20} />
          <h3>Screen Wallet Address</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="wallet-modal__field">
            <label>Wallet Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="0x..."
              className="wallet-modal__input"
              autoFocus
            />
          </div>
          <div className="wallet-modal__field">
            <label>Network</label>
            <select
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              className="wallet-modal__select"
            >
              {Object.entries(networkConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
          <div className="wallet-modal__actions">
            <button type="button" onClick={onClose} className="wallet-modal__btn wallet-modal__btn--secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!address.trim() || screenWallet.isPending}
              className="wallet-modal__btn wallet-modal__btn--primary"
            >
              {screenWallet.isPending ? <Loader2 size={14} className="spin" /> : <Shield size={14} />}
              Screen Wallet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main panel component
export default function WalletScreeningPanel({ applicantId }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const { data, isLoading, refetch, isRefetching } = useWalletScreenings(applicantId);
  const resolveScreening = useResolveWalletScreening();
  const { showToast } = useToast();

  const handleResolve = async (screeningId, resolution, notes) => {
    try {
      await resolveScreening.mutateAsync({ screeningId, resolution, notes });
      showToast('Screening resolved', 'success');
      refetch();
    } catch (error) {
      showToast(error.message || 'Failed to resolve screening', 'error');
      throw error;
    }
  };

  const screenings = data?.items || [];

  return (
    <div className="wallet-panel">
      <style>{`
        .wallet-panel {
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 1px solid var(--border-color);
          padding: 20px;
        }

        .wallet-panel__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .wallet-panel__title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .wallet-panel__actions {
          display: flex;
          gap: 8px;
        }

        .wallet-panel__btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .wallet-panel__btn--primary {
          background: var(--accent-primary);
          color: white;
          border: none;
        }

        .wallet-panel__btn--primary:hover {
          background: var(--accent-secondary);
        }

        .wallet-panel__btn--secondary {
          background: transparent;
          color: var(--text-secondary);
          border: 1px solid var(--border-color);
        }

        .wallet-panel__btn--secondary:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .wallet-panel__btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .wallet-panel__empty {
          text-align: center;
          padding: 32px;
          color: var(--text-muted);
        }

        .wallet-panel__empty-icon {
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .wallet-card {
          background: var(--bg-tertiary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
          margin-bottom: 8px;
          overflow: hidden;
        }

        .wallet-card--success { border-left: 3px solid var(--color-success); }
        .wallet-card--warning { border-left: 3px solid var(--color-warning); }
        .wallet-card--danger { border-left: 3px solid var(--color-error); }

        .wallet-card__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          cursor: pointer;
        }

        .wallet-card__header:hover {
          background: var(--bg-hover);
        }

        .wallet-card__main {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .wallet-card__address {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px;
          color: var(--text-primary);
        }

        .wallet-card__explorer-link {
          color: var(--text-muted);
          opacity: 0;
          transition: opacity 0.15s;
        }

        .wallet-card__header:hover .wallet-card__explorer-link {
          opacity: 1;
        }

        .wallet-card__explorer-link:hover {
          color: var(--accent-primary);
        }

        .wallet-card__meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
        }

        .wallet-card__network {
          color: var(--network-color, var(--text-muted));
          font-weight: 500;
        }

        .wallet-card__provider {
          color: var(--text-muted);
          text-transform: capitalize;
        }

        .wallet-card__status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .wallet-card--success .wallet-card__status { color: var(--color-success); }
        .wallet-card--warning .wallet-card__status { color: var(--color-warning); }
        .wallet-card--danger .wallet-card__status { color: var(--color-error); }

        .wallet-risk-badge {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }

        .wallet-risk-badge--success {
          background: var(--color-success-bg);
          color: var(--color-success);
        }

        .wallet-risk-badge--warning {
          background: var(--color-warning-bg);
          color: var(--color-warning);
        }

        .wallet-risk-badge--danger {
          background: var(--color-error-bg);
          color: var(--color-error);
        }

        .wallet-card__details {
          padding: 12px 14px;
          border-top: 1px solid var(--border-color);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .wallet-card__hits {
          background: var(--color-error-bg);
          border: 1px solid var(--color-error-border);
          border-radius: 6px;
          padding: 10px;
        }

        .wallet-card__hits-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: var(--color-error);
          margin-bottom: 6px;
        }

        .wallet-card__hit-types {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .wallet-card__hit-type {
          padding: 2px 8px;
          background: rgba(239, 68, 68, 0.15);
          border-radius: 4px;
          font-size: 11px;
          color: var(--color-error);
        }

        .wallet-card__stats {
          display: flex;
          gap: 16px;
        }

        .wallet-card__stat {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .wallet-card__stat-label {
          font-size: 11px;
          color: var(--text-muted);
        }

        .wallet-card__stat-value {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .wallet-card__risk-category {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .wallet-card__resolution {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding-top: 8px;
          border-top: 1px solid var(--border-color);
        }

        .wallet-card__resolution-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .wallet-card__select,
        .wallet-card__textarea {
          width: 100%;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          padding: 8px 10px;
          font-size: 13px;
          color: var(--text-primary);
          outline: none;
        }

        .wallet-card__select:focus,
        .wallet-card__textarea:focus {
          border-color: var(--accent-primary);
        }

        .wallet-card__textarea {
          resize: none;
          font-family: inherit;
        }

        .wallet-card__resolve-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px;
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }

        .wallet-card__resolve-btn:hover:not(:disabled) {
          background: var(--accent-secondary);
        }

        .wallet-card__resolve-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .wallet-card__resolved {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--color-success);
        }

        .wallet-card__resolved-notes {
          width: 100%;
          margin: 4px 0 0;
          font-size: 12px;
          color: var(--text-secondary);
          font-style: italic;
        }

        .wallet-card__timestamp {
          font-size: 11px;
          color: var(--text-muted);
        }

        /* Modal styles */
        .wallet-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .wallet-modal {
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 1px solid var(--border-color);
          width: 100%;
          max-width: 420px;
          padding: 20px;
        }

        .wallet-modal__header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .wallet-modal__header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .wallet-modal__field {
          margin-bottom: 12px;
        }

        .wallet-modal__field label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }

        .wallet-modal__input,
        .wallet-modal__select {
          width: 100%;
          padding: 10px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 14px;
          color: var(--text-primary);
          outline: none;
        }

        .wallet-modal__input:focus,
        .wallet-modal__select:focus {
          border-color: var(--accent-primary);
        }

        .wallet-modal__actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 16px;
        }

        .wallet-modal__btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .wallet-modal__btn--primary {
          background: var(--accent-primary);
          color: white;
          border: none;
        }

        .wallet-modal__btn--primary:hover:not(:disabled) {
          background: var(--accent-secondary);
        }

        .wallet-modal__btn--secondary {
          background: transparent;
          color: var(--text-secondary);
          border: 1px solid var(--border-color);
        }

        .wallet-modal__btn--secondary:hover {
          background: var(--bg-hover);
        }

        .wallet-modal__btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="wallet-panel__header">
        <div className="wallet-panel__title">
          <Wallet size={18} />
          Wallet Screening
        </div>
        <div className="wallet-panel__actions">
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="wallet-panel__btn wallet-panel__btn--secondary"
          >
            <RefreshCw size={14} className={isRefetching ? 'spin' : ''} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="wallet-panel__btn wallet-panel__btn--primary"
          >
            <Plus size={14} />
            Screen Wallet
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="wallet-panel__empty">
          <Loader2 size={24} className="spin wallet-panel__empty-icon" />
          <p>Loading wallet screenings...</p>
        </div>
      ) : screenings.length === 0 ? (
        <div className="wallet-panel__empty">
          <Wallet size={32} className="wallet-panel__empty-icon" />
          <p>No wallet addresses screened yet</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="wallet-panel__btn wallet-panel__btn--primary"
            style={{ margin: '12px auto 0' }}
          >
            <Plus size={14} />
            Screen First Wallet
          </button>
        </div>
      ) : (
        <div className="wallet-panel__list">
          {screenings.map((screening) => (
            <WalletCard
              key={screening.id}
              screening={screening}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddWalletModal
          applicantId={applicantId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}
